import React from 'react';

import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import IconButton from '@mui/material/IconButton';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItemNameTypography from 'components/MenuItemNameTypography';
import { MENU_ITEM_NAMES } from 'styles/constants/appearanceSectionNames';
import { MEDIUM_TOFU } from 'styles/theme/colors';

const QuantityContainer = styled('div')({
  display: 'flex',
  alignItems: 'center'
});

const Label = styled(MenuItemNameTypography)({
  marginLeft: '.5rem',
  marginRight: '.5rem'
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  bottom: 1,
  '& .MuiSvgIcon-root': {
    fill: theme?.palette?.menuItemNames?.main
  }
}));

const InputContainer = styled('div')({
  display: 'inline-block',
  marginRight: '6px'
});

const StyledInput = styled('input')(({ theme }) => {
  return {
    width: '50px',
    height: '48px',
    textAlign: 'left',
    paddingLeft: '12px',
    outline: 'none',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    },
    '&[type=number]': {
      '-moz-appearance': 'textfield'
    },
    border: `solid ${MEDIUM_TOFU}`,
    borderRadius: '3px',
    fontSize: '16px',
    color: theme?.palette?.menuItemNames?.main,
    backgroundColor: theme?.palette?.card?.background?.main,
    ...theme?.typography?.menuItemNames
  };
});

function QuantityInput({
  disableIncrement,
  label,
  onChange,
  value,
  removeItemFromCart
}) {
  const labelStyles = { variant: MENU_ITEM_NAMES };

  const handleDecrementClick = () => {
    if (value <= 1) {
      return removeItemFromCart();
    }
    onChange({
      target: {
        name,
        value: parseInt(value) - 1
      }
    });
  };

  const handleIncrementClick = () => {
    onChange({
      target: {
        name,
        value: value === '' ? 1 : parseInt(value) + 1
      }
    });
  };

  const handleCustomValue = e => {
    const val = e.target.value;
    let parsedValue = parseInt(val, 10);

    if (isNaN(parsedValue) || parsedValue <= 0) {
      parsedValue = 0;
    }

    if (parsedValue >= 999) {
      parsedValue = 999;
    }

    onChange({
      target: {
        name,
        value: parsedValue
      }
    });
  };

  return (
    <QuantityContainer>
      <StyledIconButton
        name="decrement"
        onClick={handleDecrementClick}
        aria-label="decrement item quantity"
        data-testid="menu-item-detail-decrement-button"
      >
        {value > 1 ? (
          <RemoveCircleOutline data-testid="decrement-counter" />
        ) : (
          <DeleteIcon data-testid="decrement-counter" />
        )}
      </StyledIconButton>
      <Label component="span" {...labelStyles}>
        {label}
      </Label>
      <InputContainer>
        <StyledInput
          type="number"
          value={value}
          onChange={handleCustomValue}
          min="0"
          data-testid="quantity-value"
          onKeyPress={e => {
            if (e.key === 'e' || isNaN(parseInt(e.key))) {
              e.preventDefault();
            }
          }}
        />
      </InputContainer>
      <StyledIconButton
        name="increment"
        aria-label="increment item quantity"
        disabled={disableIncrement}
        onClick={handleIncrementClick}
        data-testid="menu-item-detail-increment-button"
      >
        <AddCircleOutline data-testid="increment-counter" />
      </StyledIconButton>
    </QuantityContainer>
  );
}

QuantityInput.propTypes = {
  disableDecrement: PropTypes.bool,
  disableIncrement: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.number.isRequired,
  removeItemFromCart: PropTypes.func
};

QuantityInput.defaultProps = {
  value: 0
};

export default QuantityInput;
